import type { Icon } from "iconsax-react";
import {
  Box1,
  Card,
  Crown,
  Danger,
  DollarCircle,
  Hashtag,
  Instagram,
  MessageText1,
  Messages2,
  People,
  Send2,
  TruckFast,
  Shop,
  Task,
  Whatsapp,
} from "iconsax-react";

import { CreateSocialNetworkDtoPlatform } from "@/api/schemas/createSocialNetworkDtoPlatform";
import type { RoleTone } from "@/components/Organisms/SettingsTeamPanel/SettingsTeamPanel.styles";
import type { SettingsTabId } from "@/components/Templates/Settings/Settings.template.types";

export type ShopRoleKey = "owner" | "operator" | "shipping" | "finance" | "product";

export type ShopRoleDefinition = {
  key: ShopRoleKey;
  labelKey: string;
  descriptionKey: string;
  accessKey: string;
  icon: Icon;
  badgeColor: "neutral" | "info" | "warning" | "success";
  avatarColor: "info" | "warning" | "success" | "secondary";
  /** Single colour for the role everywhere (avatar, chip, role card). */
  tone: RoleTone;
};

export const SHOP_ROLE_DEFINITIONS: ShopRoleDefinition[] = [
  {
    key: "owner",
    labelKey: "roles.owner",
    descriptionKey: "team.roleDescriptions.owner",
    accessKey: "team.roleAccess.owner",
    icon: Crown,
    badgeColor: "neutral",
    avatarColor: "secondary",
    tone: "primary",
  },
  {
    key: "operator",
    labelKey: "roles.operator",
    descriptionKey: "team.roleDescriptions.operator",
    accessKey: "team.roleAccess.operator",
    icon: Task,
    badgeColor: "info",
    avatarColor: "info",
    tone: "info",
  },
  {
    key: "shipping",
    labelKey: "roles.shipping",
    descriptionKey: "team.roleDescriptions.shipping",
    accessKey: "team.roleAccess.shipping",
    icon: TruckFast,
    badgeColor: "warning",
    avatarColor: "warning",
    tone: "warning",
  },
  {
    key: "finance",
    labelKey: "roles.finance",
    descriptionKey: "team.roleDescriptions.finance",
    accessKey: "team.roleAccess.finance",
    icon: DollarCircle,
    badgeColor: "success",
    avatarColor: "success",
    tone: "success",
  },
  {
    key: "product",
    labelKey: "roles.product",
    descriptionKey: "team.roleDescriptions.product",
    accessKey: "team.roleAccess.product",
    icon: Box1,
    badgeColor: "neutral",
    avatarColor: "secondary",
    tone: "secondary",
  },
];

export const INVITABLE_ROLE_KEYS: ShopRoleKey[] = ["operator", "shipping", "finance", "product"];

export const ROLE_NAME_ALIASES: Record<string, ShopRoleKey> = {
  owner: "owner",
  مالک: "owner",
  "مالک فروشگاه": "owner",
  operator: "operator",
  اپراتور: "operator",
  "اپراتور ثبت سفارش": "operator",
  shipping: "shipping",
  "مسئول ارسال": "shipping",
  finance: "finance",
  "مدیر مالی": "finance",
  product: "product",
  "مدیر محصول": "product",
  "مدیر فروشگاه": "owner",
};

export const SETTINGS_TAB_ACCESS: Record<SettingsTabId, ShopRoleKey[]> = {
  general: ["owner", "product"],
  team: ["owner"],
  shipping: ["owner", "shipping"],
  socials: ["owner", "product"],
  instagram: ["owner", "product"],
  billing: ["owner", "finance"],
  danger: ["owner"],
};

export const SETTINGS_TAB_META: Record<
  SettingsTabId,
  { icon: Icon; titleKey: string; subtitleKey: string }
> = {
  general: { icon: Shop, titleKey: "general.title", subtitleKey: "general.subtitle" },
  team: { icon: People, titleKey: "team.title", subtitleKey: "team.subtitle" },
  shipping: { icon: TruckFast, titleKey: "shipping.title", subtitleKey: "shipping.subtitle" },
  socials: { icon: Hashtag, titleKey: "socials.title", subtitleKey: "socials.subtitle" },
  instagram: { icon: Instagram, titleKey: "instagram.title", subtitleKey: "instagram.subtitle" },
  billing: { icon: Card, titleKey: "billing.title", subtitleKey: "billing.subtitle" },
  danger: { icon: Danger, titleKey: "danger.title", subtitleKey: "danger.subtitle" },
};

export const CATEGORY_OPTIONS = [
  { value: "skincare", labelKey: "categories.skincare" },
  { value: "clothing", labelKey: "categories.clothing" },
  { value: "food", labelKey: "categories.food" },
  { value: "home", labelKey: "categories.home" },
  { value: "other", labelKey: "categories.other" },
] as const;

export const BUSINESS_TYPE_OPTIONS = [
  { value: "individual", labelKey: "businessTypes.individual" },
  { value: "brand", labelKey: "businessTypes.brand" },
  { value: "team", labelKey: "businessTypes.team" },
] as const;

export const SHIPPING_METHOD_KEYS = ["post", "courier", "pickup"] as const;

export type ShippingMethodKey = (typeof SHIPPING_METHOD_KEYS)[number];

export const SHIPPING_METHOD_META: Record<
  ShippingMethodKey,
  { icon: Icon; titleKey: string; descKey: string }
> = {
  post: { icon: Box1, titleKey: "shipping.methods.post", descKey: "shipping.methods.postDesc" },
  courier: {
    icon: TruckFast,
    titleKey: "shipping.methods.courier",
    descKey: "shipping.methods.courierDesc",
  },
  pickup: {
    icon: Shop,
    titleKey: "shipping.methods.pickup",
    descKey: "shipping.methods.pickupDesc",
  },
};

export const PAYMENT_GATEWAY_OPTIONS = [
  { value: "zarinpal", labelKey: "shipping.gateways.zarinpal" },
  { value: "idpay", labelKey: "shipping.gateways.idpay" },
  { value: "nextpay", labelKey: "shipping.gateways.nextpay" },
] as const;

export const SMS_OPTION_KEYS = ["sms_order_confirm", "sms_tracking", "sms_status_update"] as const;

export type SmsOptionKey = (typeof SMS_OPTION_KEYS)[number];

export const SMS_OPTION_META: Record<SmsOptionKey, { titleKey: string; descKey: string }> = {
  sms_order_confirm: {
    titleKey: "shipping.sms.confirm",
    descKey: "shipping.sms.confirmDesc",
  },
  sms_tracking: {
    titleKey: "shipping.sms.tracking",
    descKey: "shipping.sms.trackingDesc",
  },
  sms_status_update: {
    titleKey: "shipping.sms.status",
    descKey: "shipping.sms.statusDesc",
  },
};

export type SocialPlatformKey = keyof typeof CreateSocialNetworkDtoPlatform;

export const SOCIAL_PLATFORM_DEFS: {
  key: SocialPlatformKey;
  labelKey: string;
  placeholderKey: string;
  helperKey: string;
  icon: Icon;
  colorToken: "instagram" | "telegram" | "whatsapp" | "primary" | "secondary";
  /** Brand colour of the platform, for its icon tile. */
  brandColor: string;
}[] = [
  {
    key: "instagram",
    labelKey: "socials.platforms.instagram",
    placeholderKey: "socials.placeholders.instagram",
    helperKey: "socials.helpers.instagram",
    icon: Instagram,
    colorToken: "instagram",
    brandColor: "#e1306c",
  },
  {
    key: "telegram",
    labelKey: "socials.platforms.telegram",
    placeholderKey: "socials.placeholders.telegram",
    helperKey: "socials.helpers.telegram",
    icon: Send2,
    colorToken: "telegram",
    brandColor: "#26a5d4",
  },
  {
    key: "whatsapp",
    labelKey: "socials.platforms.whatsapp",
    placeholderKey: "socials.placeholders.whatsapp",
    helperKey: "socials.helpers.whatsapp",
    icon: Whatsapp,
    colorToken: "whatsapp",
    brandColor: "#25d366",
  },
  {
    key: "bale",
    labelKey: "socials.platforms.bale",
    placeholderKey: "socials.placeholders.bale",
    helperKey: "socials.helpers.bale",
    icon: MessageText1,
    colorToken: "primary",
    brandColor: "#3fb68b",
  },
  {
    key: "rubika",
    labelKey: "socials.platforms.rubika",
    placeholderKey: "socials.placeholders.rubika",
    helperKey: "socials.helpers.rubika",
    icon: Messages2,
    colorToken: "secondary",
    brandColor: "#8a3ffc",
  },
  {
    key: "eitaa",
    labelKey: "socials.platforms.eitaa",
    placeholderKey: "socials.placeholders.eitaa",
    helperKey: "socials.helpers.eitaa",
    icon: MessageText1,
    colorToken: "secondary",
    brandColor: "#f7931e",
  },
];
